import { useState, useEffect, useRef } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  ExternalLink, Coins, Search, GraduationCap, Layers, ShieldCheck,
  RotateCcw, X, FileText, Calendar
} from 'lucide-react'
import { MultiSelectPopover, Option } from '@/components/shared/MultiSelectPopover'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'

interface ExamInfo { id: number; name: string; code: string }
interface StandardInfo { id: number; name: string }
interface AttemptInfo { id: number; title: string; status: string; score: number }

interface UserItem {
  id: number
  name: string
  email: string
  role: string
  balance: number
  credits_spent?: number
  standard?: StandardInfo | null
  enrolled_exams?: ExamInfo[]
  attempts_count?: number
  completed_attempts_count?: number
  recent_attempts?: AttemptInfo[]
  created_at: string
}

interface FilterOption { id: number | string; name: string }

interface Filters {
  roles: string[]
  exams: FilterOption[]
  standards: FilterOption[]
  credit_ranges: FilterOption[]
}

interface Meta extends PaginationMeta {}

interface Props {
  users: UserItem[]
  meta: Meta
  filters?: Filters
}

interface UserContextBadgesProps {
  enrolledExams?: ExamInfo[]
  standard?: StandardInfo | null
  attemptsCount?: number
  recentAttempts?: AttemptInfo[]
  balance?: number
  creditsSpent?: number
  createdAt?: string
  returnUrl: string
  isLastRow?: boolean
  isFirstRow?: boolean
}

function UserContextBadges({
  enrolledExams = [],
  standard,
  attemptsCount = 0,
  recentAttempts = [],
  creditsSpent = 0,
  createdAt,
  returnUrl,
  isLastRow = false,
  isFirstRow = false,
}: UserContextBadgesProps) {
  const [activeTab, setActiveTab] = useState<'exams' | 'attempts' | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setActiveTab(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleTab = (tab: 'exams' | 'attempts') => {
    setActiveTab((prev) => (prev === tab ? null : tab))
  }

  const cleanReturnUrl = returnUrl ? returnUrl.replace(/^https?:\/\/[^\/]+/, '') : '/admin/users'
  const openUpward = isLastRow && !isFirstRow

  return (
    <div className="relative inline-flex flex-wrap items-center gap-1.5 pt-1" ref={containerRef}>
      {/* Enrolled Exams Badge */}
      {enrolledExams.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('exams')}
          className={`gap-1 text-[10px] px-2 py-0.5 cursor-pointer font-normal transition-colors ${
            activeTab === 'exams'
              ? 'bg-primary/20 text-primary border-primary/40 font-medium'
              : 'bg-primary/10 text-primary border-primary/25 hover:bg-primary/15'
          }`}
        >
          <GraduationCap className="h-3 w-3 text-primary" />
          <span>{enrolledExams.length} {enrolledExams.length === 1 ? enrolledExams[0].name : 'Exams'}</span>
        </Badge>
      )}

      {/* Standard Badge */}
      {standard && (
        <Badge variant="outline" className="gap-1 text-[10px] px-2 py-0.5 font-normal bg-secondary/10 text-secondary border-secondary/25">
          <Layers className="h-3 w-3 text-secondary" />
          <span>{standard.name}</span>
        </Badge>
      )}

      {/* Attempt Activity Badge */}
      {attemptsCount > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('attempts')}
          className={`gap-1 text-[10px] px-2 py-0.5 cursor-pointer font-normal transition-colors ${
            activeTab === 'attempts'
              ? 'bg-primary/20 text-primary border-primary/40 font-medium'
              : 'bg-muted/60 text-foreground border-border/80 hover:bg-muted'
          }`}
        >
          <FileText className="h-3 w-3 text-primary" />
          <span>{attemptsCount} {attemptsCount === 1 ? 'Attempt' : 'Attempts'}</span>
        </Badge>
      )}

      {/* Credits Spent Sub-Badge */}
      {creditsSpent > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] px-2 py-0.5 font-normal bg-muted/60 text-muted-foreground border-border/80">
          <Coins className="h-3 w-3 text-primary" />
          <span>{creditsSpent} Credits Spent</span>
        </Badge>
      )}

      {/* Joined Date */}
      {createdAt && (
        <Badge variant="outline" className="gap-1 text-[10px] px-2 py-0.5 font-normal bg-muted/40 text-muted-foreground border-border/60">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span>Joined {createdAt}</span>
        </Badge>
      )}

      {/* Unified Popover Dropdown */}
      {activeTab === 'exams' && enrolledExams.length > 0 && (
        <div
          className={`absolute left-0 w-60 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-[100] p-2.5 text-xs space-y-1.5 animate-in fade-in-50 zoom-in-95 ${
            openUpward ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          <div className="font-semibold text-[11px] text-muted-foreground pb-1.5 border-b border-border flex justify-between items-center">
            <span className="font-semibold text-foreground">Enrolled Exams ({enrolledExams.length})</span>
            <X className="h-3.5 w-3.5 cursor-pointer hover:text-foreground text-muted-foreground" onClick={() => setActiveTab(null)} />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5 pt-0.5">
            {enrolledExams.map((ex, idx) => (
              <Link
                key={`exam-${ex.id}-${idx}`}
                href={`/admin/exams/${ex.id}?return_to=${encodeURIComponent(cleanReturnUrl)}`}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-lg hover:bg-accent hover:text-accent-foreground text-primary font-medium text-xs truncate transition-colors"
              >
                <span className="truncate">{ex.name} ({ex.code})</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ml-2" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'attempts' && recentAttempts.length > 0 && (
        <div
          className={`absolute left-0 w-64 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-[100] p-2.5 text-xs space-y-1.5 animate-in fade-in-50 zoom-in-95 ${
            openUpward ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          <div className="font-semibold text-[11px] text-muted-foreground pb-1.5 border-b border-border flex justify-between items-center">
            <span className="font-semibold text-foreground">Recent Attempts ({recentAttempts.length})</span>
            <X className="h-3.5 w-3.5 cursor-pointer hover:text-foreground text-muted-foreground" onClick={() => setActiveTab(null)} />
          </div>
          <div className="max-h-44 overflow-y-auto space-y-1 pt-0.5">
            {recentAttempts.map((att, idx) => (
              <Link
                key={`attempt-${att.id}-${idx}`}
                href={`/student/attempts/${att.id}/result?return_to=${encodeURIComponent(cleanReturnUrl)}`}
                className="p-2 rounded-lg bg-muted/40 text-[11px] space-y-0.5 block hover:bg-accent hover:border-primary/40 border border-border/60 transition-colors group"
              >
                <div className="font-medium text-foreground truncate flex items-center justify-between">
                  <span className="truncate group-hover:underline">{att.title}</span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ml-1 group-hover:text-primary" />
                </div>
                <div className="flex justify-between items-center text-muted-foreground">
                  <span className="capitalize">{att.status.replace('_', ' ')}</span>
                  <span className="font-semibold text-foreground">{att.score ?? 0} pts</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminUsersIndex({ users, meta, filters }: Props) {
  const getArrayParam = (key: string): string[] => {
    if (typeof window === 'undefined') return []
    const sp = new URLSearchParams(window.location.search)
    const values: string[] = []
    sp.forEach((val, k) => {
      if (k === key || k.startsWith(`${key}[`)) {
        if (val && !values.includes(val)) values.push(val)
      }
    })
    return values
  }

  const currentParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()

  const [search, setSearch] = useState(currentParams.get('q') || '')
  const [selectedRoles, setSelectedRoles] = useState<string[]>(getArrayParam('roles'))
  const [selectedExams, setSelectedExams] = useState<string[]>(getArrayParam('exam_ids'))
  const [selectedStandards, setSelectedStandards] = useState<string[]>(getArrayParam('standard_ids'))
  const [selectedCreditRanges, setSelectedCreditRanges] = useState<string[]>(getArrayParam('credit_ranges'))

  // Filter Options
  const roleOptions: Option[] = (filters?.roles || ['student', 'reviewer', 'admin']).map((r) => ({
    id: r,
    label: r.charAt(0).toUpperCase() + r.slice(1),
  }))

  const examOptions: Option[] = (filters?.exams || []).map((e) => ({
    id: e.id,
    label: e.name,
  }))

  const standardOptions: Option[] = (filters?.standards || []).map((st) => ({
    id: st.id,
    label: st.name,
  }))

  const creditOptions: Option[] = (filters?.credit_ranges || [
    { id: 'has_credits', name: 'Has Credit Balance (>0)' },
    { id: 'zero', name: 'Zero Balance (0)' }
  ]).map((cr) => ({
    id: cr.id,
    label: cr.name,
  }))

  const applyFilters = (overrides?: Record<string, any>) => {
    const query: Record<string, any> = {}

    const qVal = overrides && 'q' in overrides ? overrides.q : search
    if (qVal) query.q = qVal

    const rVal = overrides && 'roles' in overrides ? overrides.roles : selectedRoles
    if (rVal.length > 0) query['roles[]'] = rVal

    const exVal = overrides && 'exam_ids' in overrides ? overrides.exam_ids : selectedExams
    if (exVal.length > 0) query['exam_ids[]'] = exVal

    const stdVal = overrides && 'standard_ids' in overrides ? overrides.standard_ids : selectedStandards
    if (stdVal.length > 0) query['standard_ids[]'] = stdVal

    const crVal = overrides && 'credit_ranges' in overrides ? overrides.credit_ranges : selectedCreditRanges
    if (crVal.length > 0) query['credit_ranges[]'] = crVal

    const pageVal = overrides && 'page' in overrides ? overrides.page : undefined
    if (pageVal) query.page = pageVal

    const perPageVal = overrides && 'per_page' in overrides ? overrides.per_page : (currentParams.get('per_page') || (meta?.per_page ? String(meta.per_page) : undefined))
    if (perPageVal) query.per_page = perPageVal

    router.get(window.location.pathname, query, { preserveState: true, replace: true })
  }

  const clearAllFilters = () => {
    setSearch('')
    setSelectedRoles([])
    setSelectedExams([])
    setSelectedStandards([])
    setSelectedCreditRanges([])
    router.get(window.location.pathname, {}, { preserveState: true, replace: true })
  }

  const totalActiveFiltersCount =
    (search ? 1 : 0) +
    selectedRoles.length +
    selectedExams.length +
    selectedStandards.length +
    selectedCreditRanges.length

  return (
    <AppShell>
      <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto w-full min-w-0 pb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-serif text-foreground tracking-tight">User Management</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">View users, grant test credits, and update roles</p>
          </div>
        </div>

        {/* Filters Section */}
        <div className="space-y-2.5 bg-card border border-border/80 rounded-2xl p-3 sm:p-4 shadow-glass-xs">
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  applyFilters({ q: e.target.value })
                }}
                className="pl-8 h-8 sm:h-9 text-xs w-full bg-background"
              />
            </div>

            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
              <MultiSelectPopover
                title="Role"
                options={roleOptions}
                selectedValues={selectedRoles}
                onSelectionChange={(newVal) => {
                  const arr = newVal.map(String)
                  setSelectedRoles(arr)
                  applyFilters({ roles: arr })
                }}
                icon={<ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />}
                className="w-full sm:w-auto h-8 sm:h-9 text-xs justify-between"
              />

              <MultiSelectPopover
                title="Enrolled Exam"
                options={examOptions}
                selectedValues={selectedExams}
                onSelectionChange={(newVal) => {
                  const arr = newVal.map(String)
                  setSelectedExams(arr)
                  applyFilters({ exam_ids: arr })
                }}
                icon={<GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />}
                className="w-full sm:w-auto h-8 sm:h-9 text-xs justify-between"
              />

              <MultiSelectPopover
                title="Standard"
                options={standardOptions}
                selectedValues={selectedStandards}
                onSelectionChange={(newVal) => {
                  const arr = newVal.map(String)
                  setSelectedStandards(arr)
                  applyFilters({ standard_ids: arr })
                }}
                icon={<Layers className="h-3.5 w-3.5 text-muted-foreground" />}
                className="w-full sm:w-auto h-8 sm:h-9 text-xs justify-between"
              />

              <MultiSelectPopover
                title="Credit Balance"
                options={creditOptions}
                selectedValues={selectedCreditRanges}
                onSelectionChange={(newVal) => {
                  const arr = newVal.map(String)
                  setSelectedCreditRanges(arr)
                  applyFilters({ credit_ranges: arr })
                }}
                icon={<Coins className="h-3.5 w-3.5 text-muted-foreground" />}
                className="w-full sm:w-auto h-8 sm:h-9 text-xs justify-between"
              />

              {totalActiveFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="col-span-2 sm:col-span-1 h-8 sm:h-9 text-xs text-muted-foreground hover:text-foreground gap-1 justify-center"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {/* Active Filter Chips */}
          {totalActiveFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-border/50 text-xs">
              <span className="text-[11px] text-muted-foreground font-medium mr-1">Active Filters:</span>

              {selectedRoles.map((r) => (
                <Badge key={`r-${r}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2 capitalize">
                  Role: {r}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedRoles.filter((role) => role !== r)
                      setSelectedRoles(updated)
                      applyFilters({ roles: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedExams.map((exId) => {
                const ex = examOptions.find((e) => String(e.id) === String(exId))
                return (
                  <Badge key={`ex-${exId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Exam: {ex?.label || `#${exId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedExams.filter((id) => id !== exId)
                        setSelectedExams(updated)
                        applyFilters({ exam_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedStandards.map((stId) => {
                const st = standardOptions.find((std) => String(std.id) === String(stId))
                return (
                  <Badge key={`std-${stId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Standard: {st?.label || `#${stId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedStandards.filter((id) => id !== stId)
                        setSelectedStandards(updated)
                        applyFilters({ standard_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedCreditRanges.map((crId) => {
                const cr = creditOptions.find((c) => String(c.id) === String(crId))
                return (
                  <Badge key={`cr-${crId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Credits: {cr?.label || crId}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedCreditRanges.filter((id) => id !== crId)
                        setSelectedCreditRanges(updated)
                        applyFilters({ credit_ranges: updated })
                      }}
                    />
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        {/* Top Pagination (Hidden on mobile to save vertical space) */}
        <div className="hidden sm:block">
          <Pagination
            meta={meta}
            onPageChange={(page) => applyFilters({ page })}
            onPerPageChange={(per_page) => applyFilters({ page: 1, per_page })}
            itemLabel="users"
          />
        </div>

        {/* Users List: Mobile Cards (<md) & Desktop Table (>=md) */}
        {users.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground text-xs">
            No users match the selected filters. Try clearing some filters.
          </Card>
        ) : (
          <>
            {/* Mobile Cards List */}
            <div className="md:hidden space-y-2.5">
              {users.map((u, idx) => (
                <div
                  key={u.id}
                  className="bg-card border border-border/80 rounded-xl p-3.5 shadow-glass-xs space-y-2.5 hover:border-border transition-colors"
                >
                  {/* Card Header: Name, Email & Role */}
                  <div className="flex items-start justify-between gap-2 border-b border-border/50 pb-2">
                    <div className="space-y-0.5 min-w-0 flex-1">
                      <Link
                        href={`/admin/users/${u.id}`}
                        className="font-bold text-sm text-foreground hover:text-primary transition-colors block truncate"
                      >
                        {u.name}
                      </Link>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                    <Badge variant={u.role === 'admin' ? 'default' : u.role === 'reviewer' ? 'secondary' : 'outline'} className="capitalize text-[10px] shrink-0 font-semibold px-2 py-0.5">
                      {u.role}
                    </Badge>
                  </div>

                  {/* Context Badges (Exams, Standard, Attempts, Spent) */}
                  <div className="space-y-1">
                    <UserContextBadges
                      enrolledExams={u.enrolled_exams}
                      standard={u.standard}
                      attemptsCount={u.attempts_count}
                      recentAttempts={u.recent_attempts}
                      creditsSpent={u.credits_spent}
                      createdAt={u.created_at}
                      returnUrl={typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/admin/users'}
                      isFirstRow={idx === 0}
                      isLastRow={idx > 0 && idx >= users.length - 2}
                    />
                  </div>

                  {/* Card Footer: Balance, Joined & Action Link */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/50 text-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <Coins className="h-3.5 w-3.5 text-primary" />
                      <span>{u.balance} Credits</span>
                    </div>

                    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs text-primary font-medium rounded-lg gap-1 border-primary/20 hover:bg-primary/10" asChild>
                      <Link href={`/admin/users/${u.id}`}>
                        <span>View Profile</span>
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <Card className="hidden md:block overflow-visible border border-border rounded-xl">
              <CardContent className="p-0 overflow-visible">
                <Table className="overflow-visible">
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="w-28">Role</TableHead>
                      <TableHead className="w-36">Credit Balance</TableHead>
                      <TableHead className="w-32">Joined</TableHead>
                      <TableHead className="w-16 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u, idx) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium py-3 space-y-1">
                          <Link href={`/admin/users/${u.id}`} className="hover:underline text-sm font-semibold text-foreground">
                            {u.name}
                          </Link>
                          <div className="text-xs text-muted-foreground">{u.email}</div>

                          <UserContextBadges
                            enrolledExams={u.enrolled_exams}
                            standard={u.standard}
                            attemptsCount={u.attempts_count}
                            recentAttempts={u.recent_attempts}
                            creditsSpent={u.credits_spent}
                            createdAt={u.created_at}
                            returnUrl={typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/admin/users'}
                            isFirstRow={idx === 0}
                            isLastRow={idx > 0 && idx >= users.length - 2}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.role === 'admin' ? 'default' : u.role === 'reviewer' ? 'secondary' : 'outline'} className="capitalize">
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-sm">
                          <span className="flex items-center gap-1 text-foreground">
                            <Coins className="h-3.5 w-3.5 text-primary" />
                            {u.balance}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{u.created_at}</TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" asChild title="View User Details">
                            <Link href={`/admin/users/${u.id}`}>
                              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {/* Bottom Pagination */}
        <Pagination
          meta={meta}
          onPageChange={(page) => applyFilters({ page })}
          onPerPageChange={(per_page) => applyFilters({ page: 1, per_page })}
          itemLabel="users"
        />
      </div>
    </AppShell>
  )
}
